import { Icon, Tooltip, Typography, strIsNullOrEmpty } from '@fox/atoms';
import { Flex } from '@fox/layout';
import DOMPurify from 'dompurify';
import * as moment from 'moment';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import { useTheme } from 'styled-components';
import { useComponentStyleContext } from '../component-style-context';
import { NewsListItemModel } from './contracts';
import { Compact, CompactItemsWrapper, InnerCompact, ResponsiveItemWrapper, transitionStyles } from './shared';
import { StarRating } from './star-rating';

function CompactNewsItem({
	newsItem,
	openLinkInNewTab,
	hideThumbnail,
	numberOfReadMoreLines,
	showFullReadMore,
	readMoreTextColour
}: {
	newsItem: NewsListItemModel;
	openLinkInNewTab: boolean;
	hideThumbnail?: boolean;
	showFullReadMore?: boolean;
	numberOfReadMoreLines?: number;
	readMoreTextColour?: string;
}) {
	const date = newsItem.publishDateFormatted ?? moment(newsItem.publishDate).format('DD MMM YYYY');
	const theme = useTheme();
	const readMoreHeight = showFullReadMore && numberOfReadMoreLines === null ? 'auto' : `${(numberOfReadMoreLines ?? 2) * 25}px`;
	const { headerFont, headerFontColour, font, fontColour } = useComponentStyleContext();
	return (
		<Transition timeout={200} in={true} appear={true}>
			{(state: TransitionStatus) => {
				return (
					<a
						style={{
							textDecoration: 'none',
							color: 'inherit',
							width: 'max-content',
							display: 'block'
						}}
						href={newsItem.link}
						target={openLinkInNewTab ? '_blank' : '_self'}
					>
						<Compact
							style={{ ...transitionStyles[state], textAlign: 'left' }}
							$hasImage={
								!hideThumbnail &&
								(!strIsNullOrEmpty(newsItem.mobileThumbnailUrl) || !strIsNullOrEmpty(newsItem.thumbnailUrl))
							}
							$showFullReadMore={showFullReadMore}
							$numberOfReadMoreLines={numberOfReadMoreLines}
						>
							{!hideThumbnail ? (
								<img
									src={newsItem.mobileThumbnailUrl || newsItem.thumbnailUrl}
									style={{
										borderRadius: '10px 10px 0px 0px',
										width: '100%',
										height: '200px',
										objectFit: 'cover'
									}}
								/>
							) : null}
							<InnerCompact>
								<Typography
									style={{
										fontSize: '0.8rem',
										...font,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										width: '100%'
									}}
								>
									<span
										style={{
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											flex: 1
										}}
									>
										{newsItem.tags?.map((tag, i) => {
											return (
												<span
													key={i}
													style={{
														color: theme.palette.primary.main,
														fontWeight: 'bold'
													}}
												>{`${tag}${i < newsItem.tags.length - 1 ? ', ' : ''}`}</span>
											);
										})}
										{date && newsItem.publishDateFormatted && newsItem.tags?.length ? <> | </> : null}
										{date && newsItem.publishDateFormatted ? date : null}
									</span>
									{newsItem.averageRating && newsItem.averageRating > 0 ? (
										<StarRating rating={newsItem.averageRating} customColor={fontColour?.color} fontSize="1rem" />
									) : null}
								</Typography>
								<Tooltip title={newsItem.title}>
									<Typography
										variant="h4"
										style={{
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											fontSize: '1.3rem',
											fontWeight: 'bold',
											...headerFont,
											...headerFontColour
										}}
									>
										{newsItem.truncatedTitle}
									</Typography>
								</Tooltip>
								{showFullReadMore ? (
									<div
										className="read-more"
										style={{ height: readMoreHeight }}
										dangerouslySetInnerHTML={{
											__html: DOMPurify.sanitize(newsItem.readMoreSummary) || DOMPurify.sanitize(newsItem.summary)
										}}
									/>
								) : (
									<Typography
										className="read-more"
										style={{ height: readMoreHeight, fontSize: '1rem', ...font, ...fontColour }}
									>
										{newsItem.readMoreSummary || newsItem.summary || ''}
									</Typography>
								)}

								<Flex
									style={{
										width: '100%',
										alignItems: 'center',
										justifyContent: 'center',
										marginTop: '1rem'
									}}
								>
									<Flex style={{ alignItems: 'center' }}>
										<Typography
											style={{
												fontWeight: 'bold',
												color: readMoreTextColour || theme.palette.primary.main,
												marginRight: '1rem',
												...font
											}}
										>
											Read More
										</Typography>
										<Icon iconName="ArrowForwardIos" customColor={readMoreTextColour || theme.palette.primary.main} />
									</Flex>
								</Flex>
							</InnerCompact>
						</Compact>
					</a>
				);
			}}
		</Transition>
	);
}

export function CompactView({
	openLinkInNewTab,
	newsItems,
	hideThumbnail,
	showFullReadMore,
	numberOfReadMoreLines,
	readMoreTextColour
}: {
	openLinkInNewTab: boolean;
	newsItems: NewsListItemModel[];
	hideThumbnail?: boolean;
	showFullReadMore?: boolean;
	numberOfReadMoreLines?: number;
	readMoreTextColour?: string;
}) {
	return (
		<ResponsiveItemWrapper>
			<CompactItemsWrapper>
				{newsItems?.map((newsItem, i) => {
					const id = `${i}${newsItem.truncatedTitle}`;
					return (
						<CompactNewsItem
							key={id}
							newsItem={newsItem}
							openLinkInNewTab={openLinkInNewTab}
							hideThumbnail={hideThumbnail}
							numberOfReadMoreLines={numberOfReadMoreLines}
							showFullReadMore={showFullReadMore}
							readMoreTextColour={readMoreTextColour}
						/>
					);
				})}
			</CompactItemsWrapper>
		</ResponsiveItemWrapper>
	);
}
